import type { WastewaterSummary, WastewaterSummaryRow } from '../wastewaterDataService';
import type { SectorValueStatus } from './types';
import { CANONICAL_GHG_CONTRACT } from './contract';
import {
  buildWastewaterProvenance,
  normalizeCalculationMethodFromDb,
  type WastewaterVersionProvenance,
} from './versionProvenance';
import {
  emptyWastewaterAnnualActivityDrivers,
  scaleEmissionsByActivityRatio,
  selectSharedActivityDriver,
  type WastewaterActivityDriverKind,
  type WastewaterAnnualActivityDrivers,
} from './wastewaterAnnualActivityDrivers';

/** Approved canonical target for wastewater-1.4 annual series semantics. */
export const CANONICAL_WASTEWATER_METHOD_VERSION =
  CANONICAL_GHG_CONTRACT.methodVersions.wastewaterVersions.approvedCanonicalCalculationMethod;

/** @deprecated Flat copy is no longer the default hierarchy; retained for packet/history references. */
export const CARRY_FORWARD_ESTIMATION_METHOD = 'provisional_carry_forward_latest_verified';
export const CARRY_FORWARD_SOURCE_YEAR = 2020;
export const CARRY_FORWARD_TARGET_YEARS = [2021, 2022, 2023] as const;

export const MODELED_BACKCAST_METHOD = 'modeled_backcast';
export const MODELED_INTERPOLATION_METHOD = 'modeled_interpolation';
export const MODELED_EXTRAPOLATION_METHOD = 'modeled_extrapolation';
export const MODELED_ANNUAL_ACTIVITY_METHOD = 'modeled_annual_activity';

export type WastewaterEstimationMethod =
  | typeof CARRY_FORWARD_ESTIMATION_METHOD
  | typeof MODELED_BACKCAST_METHOD
  | typeof MODELED_INTERPOLATION_METHOD
  | typeof MODELED_EXTRAPOLATION_METHOD
  | typeof MODELED_ANNUAL_ACTIVITY_METHOD
  | string;

export interface ResolvedWastewaterYear {
  inventoryYear: number;
  emissionsMt: number | null;
  status: SectorValueStatus;
  /** Verified wastewater source year when modeled or verified. */
  sourceDataYear: number | null;
  /** Activity-driver year used for scaling (often equals inventoryYear). */
  activityYear: number | null;
  activityDriverKind: WastewaterActivityDriverKind | null;
  activityDriverSourceTable: string | null;
  activityDriverSourceField: string | null;
  sourceDriverValue: number | null;
  targetDriverValue: number | null;
  scalingRatio: number | null;
  estimationMethod: WastewaterEstimationMethod | null;
  /** Actual calculation-method version - never relabeled from legacy verified source data. */
  methodVersion: string | null;
  efVersion: string;
  bestTier: number | null;
  worstTier: number | null;
  coverageStatus: string | null;
  notes: string | null;
  provenance: WastewaterVersionProvenance;
}

function provenanceFromRow(row: WastewaterSummaryRow): WastewaterVersionProvenance {
  return buildWastewaterProvenance(
    normalizeCalculationMethodFromDb(row.methodology_version),
    row.ef_version || CANONICAL_GHG_CONTRACT.methodVersions.wastewater_ef
  );
}

function normalizeRow(row: WastewaterSummaryRow): ResolvedWastewaterYear {
  const status: SectorValueStatus =
    row.coverage_status === 'uncalculable'
      ? 'unavailable'
      : row.worst_tier >= 5
        ? 'estimated'
        : row.review_required
          ? 'estimated'
          : 'calculated';

  const calculationMethod = normalizeCalculationMethodFromDb(row.methodology_version);

  return {
    inventoryYear: row.inventory_year,
    emissionsMt: row.wastewater_emissions_mtco2e,
    status,
    sourceDataYear: row.inventory_year,
    activityYear: row.inventory_year,
    activityDriverKind: null,
    activityDriverSourceTable: null,
    activityDriverSourceField: null,
    sourceDriverValue: null,
    targetDriverValue: null,
    scalingRatio: null,
    estimationMethod: null,
    methodVersion: calculationMethod,
    efVersion: row.ef_version || CANONICAL_GHG_CONTRACT.methodVersions.wastewater_ef,
    bestTier: row.best_tier,
    worstTier: row.worst_tier,
    coverageStatus: row.coverage_status,
    notes:
      row.ch4_mtco2e != null && row.n2o_mtco2e != null
        ? `CH4 ${row.ch4_mtco2e} + N2O ${row.n2o_mtco2e} MtCO2e; biosolids and treatment energy excluded`
        : 'Biosolids and treatment energy excluded per wastewater boundary',
    provenance: provenanceFromRow(row),
  };
}

function unavailableYear(
  year: number,
  note: string,
  missingNotes: string[] = []
): ResolvedWastewaterYear {
  const combined = [note, ...missingNotes.filter((n) => n.includes(String(year)))].join(' ');
  return {
    inventoryYear: year,
    emissionsMt: null,
    status: 'unavailable',
    sourceDataYear: null,
    activityYear: null,
    activityDriverKind: null,
    activityDriverSourceTable: null,
    activityDriverSourceField: null,
    sourceDriverValue: null,
    targetDriverValue: null,
    scalingRatio: null,
    estimationMethod: null,
    methodVersion: null,
    efVersion: CANONICAL_GHG_CONTRACT.methodVersions.wastewater_ef,
    bestTier: null,
    worstTier: null,
    coverageStatus: null,
    notes: combined.trim() || 'Wastewater not calculated for this inventory year',
    provenance: buildWastewaterProvenance(null, null),
  };
}

function classifyModeledMethod(
  targetYear: number,
  sourceYear: number,
  verifiedYears: number[]
): typeof MODELED_BACKCAST_METHOD | typeof MODELED_INTERPOLATION_METHOD | typeof MODELED_EXTRAPOLATION_METHOD {
  const lower = verifiedYears.filter((y) => y < targetYear);
  const upper = verifiedYears.filter((y) => y > targetYear);
  if (lower.length > 0 && upper.length > 0) return MODELED_INTERPOLATION_METHOD;
  if (targetYear < sourceYear) return MODELED_BACKCAST_METHOD;
  return MODELED_EXTRAPOLATION_METHOD;
}

function nearestVerifiedYear(targetYear: number, verifiedYears: number[]): number | null {
  if (verifiedYears.length === 0) return null;
  return verifiedYears.reduce((best, year) =>
    Math.abs(year - targetYear) < Math.abs(best - targetYear) ? year : best
  );
}

function modeledFromSource(
  source: ResolvedWastewaterYear,
  targetYear: number,
  emissionsMt: number,
  estimationMethod: WastewaterEstimationMethod,
  activityYear: number,
  activityDriverKind: WastewaterActivityDriverKind,
  meta?: {
    activityDriverSourceTable: string;
    activityDriverSourceField: string;
    sourceDriverValue: number;
    targetDriverValue: number;
    scalingRatio: number;
  }
): ResolvedWastewaterYear {
  return {
    inventoryYear: targetYear,
    emissionsMt,
    status: 'estimated',
    sourceDataYear: source.sourceDataYear ?? source.inventoryYear,
    activityYear,
    activityDriverKind,
    activityDriverSourceTable: meta?.activityDriverSourceTable ?? null,
    activityDriverSourceField: meta?.activityDriverSourceField ?? null,
    sourceDriverValue: meta?.sourceDriverValue ?? null,
    targetDriverValue: meta?.targetDriverValue ?? null,
    scalingRatio: meta?.scalingRatio ?? null,
    estimationMethod,
    methodVersion: CANONICAL_WASTEWATER_METHOD_VERSION,
    efVersion: source.efVersion,
    bestTier: source.bestTier,
    worstTier: source.worstTier,
    coverageStatus: source.coverageStatus,
    notes:
      `Modeled ${targetYear} wastewater (${estimationMethod}) from verified ${source.sourceDataYear ?? source.inventoryYear} ` +
      `using ${activityDriverKind.replace(/_/g, ' ')} for activity year ${activityYear}; ` +
      `source method ${source.methodVersion ?? 'unknown'} retained in metadata`,
    provenance: buildWastewaterProvenance(
      CANONICAL_WASTEWATER_METHOD_VERSION,
      source.efVersion,
      source.provenance.datasetGenerationVersion
    ),
  };
}

/**
 * Scale a verified wastewater result to a target year using annual activity drivers.
 * Returns null when required drivers cannot be resolved credibly (caller documents gap).
 */
export function modelWastewaterYearFromVerified(
  source: ResolvedWastewaterYear,
  targetYear: number,
  drivers: WastewaterAnnualActivityDrivers,
  verifiedYears: number[]
): ResolvedWastewaterYear | null {
  if (source.emissionsMt == null || source.status === 'unavailable') return null;
  const sourceYear = source.sourceDataYear ?? source.inventoryYear;
  const sourceActivity = drivers.byYear.get(sourceYear);
  const targetActivity = drivers.byYear.get(targetYear);
  const shared = selectSharedActivityDriver(sourceActivity, targetActivity);
  if (!shared) return null;

  const scaled = scaleEmissionsByActivityRatio(
    source.emissionsMt,
    shared.source.value,
    shared.target.value
  );
  const method = classifyModeledMethod(targetYear, sourceYear, verifiedYears);
  const scalingRatio = shared.target.value / shared.source.value;
  return modeledFromSource(
    source,
    targetYear,
    scaled,
    method,
    shared.target.year,
    shared.target.kind,
    {
      activityDriverSourceTable: shared.target.sourceTable,
      activityDriverSourceField: shared.target.sourceField,
      sourceDriverValue: shared.source.value,
      targetDriverValue: shared.target.value,
      scalingRatio,
    }
  );
}

function resolveModeledYear(
  targetYear: number,
  verifiedByYear: Map<number, ResolvedWastewaterYear>,
  drivers: WastewaterAnnualActivityDrivers
): ResolvedWastewaterYear | null {
  const verifiedYears = Array.from(verifiedByYear.keys()).sort((a, b) => a - b);
  if (verifiedYears.length === 0) return null;

  const lowerYears = verifiedYears.filter((y) => y < targetYear);
  const upperYears = verifiedYears.filter((y) => y > targetYear);
  const lowerYear = lowerYears.length ? lowerYears[lowerYears.length - 1] : null;
  const upperYear = upperYears.length ? upperYears[0] : null;

  if (lowerYear != null && upperYear != null) {
    const lowerSource = verifiedByYear.get(lowerYear)!;
    const upperSource = verifiedByYear.get(upperYear)!;
    const fromLower = modelWastewaterYearFromVerified(
      lowerSource,
      targetYear,
      drivers,
      verifiedYears
    );
    const fromUpper = modelWastewaterYearFromVerified(
      upperSource,
      targetYear,
      drivers,
      verifiedYears
    );
    if (fromLower?.emissionsMt != null && fromUpper?.emissionsMt != null) {
      const span = upperYear - lowerYear;
      const weightUpper = span > 0 ? (targetYear - lowerYear) / span : 0.5;
      const blended =
        fromLower.emissionsMt * (1 - weightUpper) + fromUpper.emissionsMt * weightUpper;
      return modeledFromSource(
        lowerSource,
        targetYear,
        blended,
        MODELED_INTERPOLATION_METHOD,
        fromLower.activityYear ?? targetYear,
        fromLower.activityDriverKind ?? 'community_service_population',
        {
          activityDriverSourceTable: fromLower.activityDriverSourceTable ?? 'service_population',
          activityDriverSourceField: fromLower.activityDriverSourceField ?? 'service_pop',
          sourceDriverValue: fromLower.sourceDriverValue ?? 0,
          targetDriverValue: fromLower.targetDriverValue ?? 0,
          scalingRatio: fromLower.scalingRatio ?? 0,
        }
      );
    }
    return fromLower ?? fromUpper;
  }

  const nearest = nearestVerifiedYear(targetYear, verifiedYears);
  if (nearest == null) return null;
  return modelWastewaterYearFromVerified(
    verifiedByYear.get(nearest)!,
    targetYear,
    drivers,
    verifiedYears
  );
}

/**
 * Chris-approved annual wastewater hierarchy (wastewater-1.4):
 * 1. Verified year-specific wastewater result
 * 2. Model from nearest verified result using annual activity drivers in order:
 *    wastewater flow (comparable) → dedicated wastewater/sewer service population →
 *    community service population (residents+employees) → jurisdiction population
 * 3. If required drivers cannot be resolved, leave unavailable and document the gap
 *
 * Does not copy the same verified MT into every year.
 * Does not call community service population "wastewater service population".
 */
export function resolveWastewaterInventoryYears(
  summary: WastewaterSummary,
  years: number[],
  activityDrivers?: WastewaterAnnualActivityDrivers | null
): ResolvedWastewaterYear[] {
  const drivers =
    activityDrivers ??
    emptyWastewaterAnnualActivityDrivers(
      summary.geofips,
      'Annual activity drivers were not supplied to the resolver.'
    );

  const verifiedByYear = new Map<number, ResolvedWastewaterYear>();
  summary.rows.forEach((row) => {
    if (row.wastewater_emissions_mtco2e == null) return;
    if (!Number.isFinite(Number(row.wastewater_emissions_mtco2e))) return;
    verifiedByYear.set(row.inventory_year, normalizeRow(row));
  });

  return years.map((year) => {
    const sameYear = verifiedByYear.get(year);
    if (sameYear) return sameYear;

    const modeled = resolveModeledYear(year, verifiedByYear, drivers);
    if (modeled) return modeled;

    const yearNotes = drivers.missingDriverNotes.filter((n) => n.includes(String(year)));
    const baseNote =
      verifiedByYear.size === 0
        ? 'Wastewater not calculated for this inventory year'
        : `Could not model ${year} wastewater: missing comparable annual activity drivers for scaling from the nearest verified year.`;
    return unavailableYear(year, baseNote, yearNotes);
  });
}

/** Chart values - null for unavailable; numeric for verified or modeled years. */
export function resolvedWastewaterChartValues(
  resolved: ResolvedWastewaterYear[]
): (number | null)[] {
  return resolved.map((r) =>
    r.emissionsMt != null && r.status !== 'unavailable' ? Math.round(r.emissionsMt) : null
  );
}

export function isModeledWastewaterEstimationMethod(
  method: string | null | undefined
): boolean {
  if (!method) return false;
  return (
    method === MODELED_BACKCAST_METHOD ||
    method === MODELED_INTERPOLATION_METHOD ||
    method === MODELED_EXTRAPOLATION_METHOD ||
    method === MODELED_ANNUAL_ACTIVITY_METHOD ||
    method === CARRY_FORWARD_ESTIMATION_METHOD
  );
}
